/* Gift Theory — Personalization fee bridge + cart cleanup.

   Two responsibilities:
   1) ADD on PDP:  when the user fills any personalizer field and clicks
      "Add to cart", also POST the hidden "Допълнителна персонализация" product
      (€1) to /cart/add.js. Both lines share a `_personalization_id` so we can
      pair them later.
   2) CLEAN on cart:  if the user removes a main personalized line from the
      cart, also remove the matched fee line. Same in reverse — if only the fee
      line is left orphaned (no main with the same id), nuke it. Runs on every
      cart fetch + on a MutationObserver of the cart drawer / cart page so it
      reconciles after Dawn's own updates. */

(function () {
  'use strict';

  // Idempotent — this script loads from both gt-personalizer (PDP) and
  // theme.liquid (every page) so the cart-cleanup watcher works site-wide.
  if (window.__gtPersonalizationFeeInit) return;
  window.__gtPersonalizationFeeInit = true;

  const CFG = window.GT_PERSONALIZE_FEE || {};
  if (CFG.enabled === false) return; // toggle off via section setting

  const FEE_VARIANT_ID = Number(CFG.variantId) || 57755470889308;
  const FEE_PROPERTY_KEY = '_personalization_id';

  // ---------- PDP: add fee on form submit ----------

  let isAdding = false;

  function initPdp() {
    const pers = document.querySelector('.gt-pers');
    if (!pers) return;
    const productForm = document.querySelector(
      'product-form form[action*="/cart/add"]'
    );
    if (!productForm) return;
    productForm.addEventListener('submit', handleProductFormSubmit, { capture: true });
  }

  function handleProductFormSubmit(e) {
    // Hard guard against double-click while a request is in flight.
    if (isAdding) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!hasPersonalization()) return; // no fields filled → no fee, default flow
    e.preventDefault();
    e.stopImmediatePropagation();

    isAdding = true;
    const form = e.currentTarget;
    const submitBtn = form.querySelector('[name="add"]');
    if (submitBtn) {
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.classList.add('loading');
      submitBtn.disabled = true;
    }

    addBothLines(form)
      .then(() => openCart(submitBtn))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[gt-personalization-fee] failed; falling back', err);
        // Re-enable so the user can retry; don't fall back to native submit
        // since that would skip the fee silently.
      })
      .finally(() => {
        isAdding = false;
        if (submitBtn) {
          submitBtn.removeAttribute('aria-busy');
          submitBtn.classList.remove('loading');
          submitBtn.disabled = false;
        }
      });
  }

  function hasPersonalization() {
    // Primary signal: the opt-in checkbox. If the user explicitly ticked
    // "Допълнителна персонализация" we add the fee — regardless of whether
    // they actually filled the fields. They asked to personalize; we honour it.
    const optin = document.querySelector('[data-gt-pers-optin]');
    if (optin) return !!optin.checked;

    // Fallback for any future PDPs without the opt-in checkbox: detect by
    // whether any personalizer field has content.
    const inputs = document.querySelectorAll(
      '.gt-pers input[name^="properties["], .gt-pers textarea[name^="properties["], .gt-pers [data-gt-pers-field]'
    );
    for (const input of inputs) {
      if (input.type === 'file') {
        if (input.files && input.files.length > 0) return true;
      } else if (input.value && String(input.value).trim() !== '') {
        return true;
      }
    }
    return false;
  }

  async function addBothLines(form) {
    const formData = new FormData(form);
    const mainVariantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || '1', 10);
    if (!mainVariantId) throw new Error('No variant id on form');

    const pairId = 'gtp-' + Date.now() + '-' + Math.floor(Math.random() * 1e6);
    const properties = {};
    formData.forEach((value, key) => {
      const m = key.match(/^properties\[(.+)\]$/);
      if (m && value && (typeof value !== 'string' || value.trim() !== '')) {
        properties[m[1]] = value;
      }
    });
    properties[FEE_PROPERTY_KEY] = pairId;

    const items = [
      { id: mainVariantId, quantity, properties },
      { id: FEE_VARIANT_ID, quantity, properties: { [FEE_PROPERTY_KEY]: pairId } }
    ];

    // Ask the same /cart/add.js call to return rendered HTML for the cart
    // drawer + the cart-icon-bubble. This avoids the race where the drawer
    // opens with stale HTML before a follow-up fetch settles.
    const cart =
      document.querySelector('cart-drawer') ||
      document.querySelector('cart-notification');
    let sectionsList = '';
    if (cart && typeof cart.getSectionsToRender === 'function') {
      try {
        sectionsList = cart.getSectionsToRender().map(function (s) { return s.id; }).join(',');
      } catch (_) { /* ignore */ }
    }
    if (!sectionsList) {
      // Sensible defaults for Dawn
      sectionsList = 'cart-drawer,cart-icon-bubble';
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items,
        sections: sectionsList,
        sections_url: window.location.pathname
      })
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error('cart/add failed: ' + res.status + ' ' + text.slice(0, 200));
    }
    const data = await res.json();

    // Hand the rendered HTML to Dawn so the drawer/bubble update BEFORE we
    // open the drawer. This kills the "drawer opens empty" race.
    if (cart && typeof cart.renderContents === 'function' && data.sections) {
      try { cart.renderContents(data); } catch (_) { /* keep going */ }
    }

    // Notify other listeners that the cart changed (the reconcile watcher
    // skips this via its own fetch-hook guard, so no loop).
    if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      try {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'gt-personalization-fee',
          productVariantId: mainVariantId,
          cartData: data
        });
      } catch (_) { /* ignore */ }
    }

    return data;
  }

  function openCart(submitBtn) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      // renderContents above already populated drawer with fresh HTML; just open.
      if (typeof drawer.open === 'function') {
        drawer.open(submitBtn);
        return;
      }
    }
    const opener = document.querySelector('[aria-controls="cart-drawer"]');
    if (opener) {
      opener.click();
    } else {
      window.location.href = '/cart';
    }
  }

  // ---------- Cart-wide cleanup: keep main + fee lines paired ----------

  let reconciling = false;

  async function reconcileCart() {
    if (reconciling) return;
    reconciling = true;
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
      if (!cart || !Array.isArray(cart.items)) return;

      // Group items by pair id
      const mainsByPair = new Map();
      const feesByPair = new Map();
      for (const item of cart.items) {
        const pairId = item.properties && item.properties[FEE_PROPERTY_KEY];
        if (!pairId) continue;
        if (item.variant_id === FEE_VARIANT_ID) {
          feesByPair.set(pairId, item);
        } else {
          mainsByPair.set(pairId, item);
        }
      }

      const updates = {}; // by line item key → new quantity
      // 1) Orphan fee lines (no matching main) → remove
      feesByPair.forEach((fee, pairId) => {
        if (!mainsByPair.has(pairId)) updates[fee.key] = 0;
      });
      // 2) Quantity mismatch (main and fee should match qty) → align fee to main
      mainsByPair.forEach((main, pairId) => {
        const fee = feesByPair.get(pairId);
        if (fee && fee.quantity !== main.quantity) {
          updates[fee.key] = main.quantity;
        }
      });

      if (Object.keys(updates).length === 0) return;

      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-GT-Personalization-Skip-Reconcile': '1'
        },
        body: JSON.stringify({ updates })
      });

      // Tell Dawn to refresh the drawer/cart UI
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      // Also trigger via PubSub if available
      if (window.PUB_SUB_EVENTS && window.publish) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'gt-personalization-fee',
          productVariantId: FEE_VARIANT_ID
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[gt-personalization-fee] reconcile failed', err);
    } finally {
      reconciling = false;
    }
  }

  function initCartWatcher() {
    // Initial reconcile on page load (catches reloads of cart page)
    reconcileCart();

    // Intercept fetch — any call to a cart-mutating endpoint should trigger a
    // reconcile after the response settles. This catches direct API calls,
    // Dawn's internal fetches, and any third-party code that hits /cart/*.
    const cartMutatePattern = /\/cart\/(change|update|add|clear)(\.js)?$/i;
    const origFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : input && input.url;
      // Read our skip header to avoid infinite loops on our own update call
      const headers = (init && init.headers) || (input && input.headers) || {};
      const skip =
        (headers instanceof Headers && headers.get('X-GT-Personalization-Skip-Reconcile')) ||
        (headers && headers['X-GT-Personalization-Skip-Reconcile']);
      const isCartMutate = !skip && url && cartMutatePattern.test(url);
      const result = origFetch(input, init);
      if (isCartMutate) {
        result.finally(() => {
          setTimeout(reconcileCart, 200);
        });
      }
      return result;
    };

    // Watch for cart drawer mutations (Dawn updates the drawer HTML on change)
    const drawer = document.querySelector('cart-drawer, #CartDrawer');
    if (drawer) {
      let scheduled = false;
      const obs = new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        // debounce: wait for Dawn's update to settle, then reconcile once
        setTimeout(() => {
          scheduled = false;
          reconcileCart();
        }, 350);
      });
      obs.observe(drawer, { childList: true, subtree: true });
    }

    // Also watch the cart page list if present
    const cartList = document.querySelector('cart-items, .cart-items, form[action="/cart"]');
    if (cartList && cartList !== drawer) {
      let scheduled = false;
      const obs2 = new MutationObserver(() => {
        if (scheduled) return;
        scheduled = true;
        setTimeout(() => {
          scheduled = false;
          reconcileCart();
        }, 350);
      });
      obs2.observe(cartList, { childList: true, subtree: true });
    }

    // Listen for Dawn's pub-sub cart events
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (data) => {
        if (data && data.source === 'gt-personalization-fee') return; // avoid self-loop
        reconcileCart();
      });
    }
  }

  function init() {
    initPdp();
    initCartWatcher();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
